import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { getAllBookmarks, saveAllBookmarks, enqueueBookmarkChange } from '@/db/viewerDb'
import { syncBookmarks } from '@/api/bookmarksApi'
import { useAppStore } from '@/store/appStore'
import { extractApiErrorMessage } from '@/utils/apiError'
import type { ApiBookmark, BookmarkChange, ContentType } from '@/types/api'

/**
 * Hook for reading bookmarks and performing bookmark mutations.
 * Reads from IndexedDB cache. Online mutations sync immediately via POST /bookmarks/sync.
 * Offline mutations are queued in the bookmark-offline-queue store and synced on reconnect.
 */
export function useBookmarks() {
  const { t } = useTranslation()
  const { isOffline, userDataSyncVersion, bumpUserDataSyncVersion } = useAppStore()
  const [bookmarks, setBookmarks] = useState<ApiBookmark[]>([])
  const [initialized, setInitialized] = useState(false)
  const [isToggling, setIsToggling] = useState(false)

  // Re-read from IndexedDB on mount and whenever useInitialSync writes fresh data
  useEffect(() => {
    getAllBookmarks().then((bm) => {
      setBookmarks(bm)
      setInitialized(true)
    })
  }, [userDataSyncVersion])

  const isBookmarked = (contentType: ContentType, entityId: number) =>
    bookmarks.some((b) => b.contentType === contentType && b.entityId === entityId)

  const toggleBookmark = async (contentType: ContentType, entityId: number) => {
    const existing = bookmarks.find(
      (b) => b.contentType === contentType && b.entityId === entityId
    )
    const isRemoving = !!existing

    if (isOffline) {
      // Queue the change for later sync
      const change: BookmarkChange = {
        clientId: crypto.randomUUID(),
        id: existing?.id ?? null,
        operation: existing ? 'Delete' : 'Create',
        contentType,
        entityId,
      }
      await enqueueBookmarkChange(change)

      // Optimistically update local state
      let updated: ApiBookmark[]
      if (existing) {
        updated = bookmarks.filter((b) => !(b.contentType === contentType && b.entityId === entityId))
      } else {
        const optimistic: ApiBookmark = {
          id: -(Date.now()), // Negative temp ID
          contentType,
          entityId,
          createdOn: new Date().toISOString(),
        }
        updated = [...bookmarks, optimistic]
      }
      await saveAllBookmarks(updated)
      setBookmarks(updated)
      bumpUserDataSyncVersion()
      toast.info(isRemoving ? t('bookmark.removedOffline') : t('bookmark.addedOffline'))
      return
    }

    // Online: sync immediately
    setIsToggling(true)
    const change: BookmarkChange = {
      clientId: crypto.randomUUID(),
      id: existing?.id ?? null,
      operation: existing ? 'Delete' : 'Create',
      contentType,
      entityId,
    }
    try {
      const response = await syncBookmarks({ changes: [change] })
      const mapped: ApiBookmark[] = response.bookmarks.map((b) => ({
        id: Number(b.id),
        contentType: b.contentType as ContentType,
        entityId: Number(b.entityId),
        createdOn: b.createdOn ?? null,
      }))
      await saveAllBookmarks(mapped)
      setBookmarks(mapped)
      bumpUserDataSyncVersion()
      toast.success(isRemoving ? t('bookmark.removed') : t('bookmark.added'))
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || (isRemoving ? t('bookmark.removeFailed') : t('bookmark.addFailed')))
    } finally {
      setIsToggling(false)
    }
  }

  const reloadBookmarks = async () => {
    const bm = await getAllBookmarks()
    setBookmarks(bm)
  }

  return { bookmarks, isBookmarked, toggleBookmark, initialized, reloadBookmarks, isToggling }
}
